// Libs
import React, { useEffect, useState } from 'react';
import {
  NetworkStatus,
  useQuery,
  useMutation,
  useApolloClient
} from '@apollo/client';
import styled from 'styled-components';

// Components
import { BinderContent, Button, TextInput } from '@ibinder/common-frontend';
import SearchableDropDown from '../form/SearchableDropDown';
import IssueTemplateItem from './IssueTemplateItem';
import IssueTemplateActionbar from './IssueTemplateActionbar';
import { generateIssueTemplateItem } from '../../utils/generateIssueTemplateItem';
import AdminModal from '../AdminBinder/AdminContentContainer/TabContent/ListContainer/AdminModal';

// Utils
import {
  GET_ALL_ORGS,
  GET_ALL_ROLE_TYPES,
  GET_ROLE_BY_ID
} from '../../api/queries/queries_local';
import { CREATE_ISSUE_TEMPLATE } from '../../api/mutations/inspectionTemplate.query';
import { issueTemplateSchema } from './IssueTemlateSchema';
import useImpersonateAdmin, {
  impersonateHeader
} from '../../utils/useImpersonateAdmin';

// Styles
import { FormContainer, StyledButton } from '../form/form.styles';
import { FORM_TYPES } from '../../utils/FormTypes';

const OverviewWrapper = styled.div`
  margin: 0 -1rem;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
  @media (max-width: ${({ theme }) => theme.devices.xs}px) {
    margin: 0 -0.5rem;
  }
`;

const ModalWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Container = styled.div`
  margin-bottom: 1rem;
`;

const ErrorField = styled.p`
  color: ${(p) => p.theme.colors.error};
`;

const initialState = [
  {
    id: 'GLOBAL_Issue_Title',
    name: 'Title',
    type: 'text',
    formTypeValue: 'TextForm',
    formLabel: 'Title for issue',
    required: true,
    locked: true,
    expanded: false,
    index: 0
  },
  {
    id: 'GLOBAL_Issue_Description',
    name: 'Description',
    type: 'text',
    formTypeValue: 'TextForm',
    formLabel: 'Description for issue',
    required: true,
    locked: true,
    expanded: false,
    index: 1
  },
  {
    id: '',
    name: 'Role selection',
    type: 'options',
    options: [],
    formLabel: 'Assign to',
    formTypeValue: FORM_TYPES.Option,
    required: true,
    locked: true,
    expanded: false,
    index: 2
  }
];

const initialSelectedOrg = {
  organization: null
};

const CreateIssueTemplate = () => {
  const client = useApolloClient();

  const [roleTypes, setRoleTypes] = useState(null);
  const [formData, setFormData] = useState(initialState);
  const [templateName, setTemplateName] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(initialSelectedOrg);
  const [selectedRoleTypeData, setSelectedRoleTypeData] = useState(null);
  const [selectedRoleType, setSelectedRoleType] = useState({
    index: '',
    role: ''
  });
  const [showDoneModal, setShowDoneModal] = useState(false);
  const showDoneModalNode = React.useRef();
  const [validationErrors, setValidationErrors] = useState([]);

  const [addIssueTemplate] = useMutation(CREATE_ISSUE_TEMPLATE);

  const orgs = useQuery(GET_ALL_ORGS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const [useAdminToken] = useImpersonateAdmin(
    client,
    orgs.data?.get[selectedOrg.organization]?.id
  );

  useEffect(() => {
    // Set selected organizations tenant id in localstorage so authLink can accesss it
    if (!useAdminToken) return;
    if (orgs.data && selectedOrg.organization !== null) {
      localStorage.setItem(
        'tenantId',
        orgs.data.get[selectedOrg.organization].id
      );
    }
  }, [useAdminToken]);

  const getRoleTypes = useQuery(GET_ALL_ROLE_TYPES, {
    skip: !useAdminToken,
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      setRoleTypes(res.get);
      setSelectedRoleType({ index: 2, role: res.get[0] });
    },
    ...impersonateHeader(useAdminToken)
  });

  const getRoleById = useQuery(GET_ROLE_BY_ID, {
    variables: {
      id: selectedRoleType && selectedRoleType?.role?.id
    },
    skip: !selectedRoleType.role && !useAdminToken,
    fetchPolicy: 'no-cache',
    onCompleted: (res) => setSelectedRoleTypeData(res.get),
    ...impersonateHeader(useAdminToken)
  });

  const renderForm = () => {
    return formData.map((item, i) => {
      return (
        <IssueTemplateItem
          item={item}
          setFormData={setFormData}
          formData={formData}
          setSelectedRoleType={setSelectedRoleType}
          roleTypes={roleTypes}
          selectedRoleType={selectedRoleType}
          key={i}
          index={i}
        />
      );
    });
  };

  const resetForm = () => {
    setRoleTypes(null);
    setFormData(initialState);
    setTemplateName('');
    setSelectedOrg(initialSelectedOrg);
    setSelectedRoleType({
      index: '',
      role: ''
    });
  };

  const mapSelectedData = (roleType, roleTypeData) => {
    let new_array = formData.map((x) =>
      x.index === roleType.index
        ? {
            ...x,
            id: selectedRoleType.role.metadataElementId,
            name: selectedRoleType.role.name,
            options: roleTypeData.map((option) => ({
              value: option.name,
              label: null
            }))
          }
        : x
    );

    return new_array;
  };

  useEffect(() => {
    if (selectedRoleTypeData && selectedRoleType) {
      setFormData(mapSelectedData(selectedRoleType, selectedRoleTypeData));
    }
  }, [selectedRoleTypeData]);

  const addNewItem = () => {
    const newId = formData.length;
    let newItem = {
      id: '',
      name: '',
      type: 'text',
      formTypeValue: 'TextForm',
      formLabel: '',
      required: false,
      locked: false,
      expanded: true,
      index: newId
    };

    setFormData((prevState) => [...prevState, newItem]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors([]);

    let postBody = {
      name: templateName,
      Items: formData.map((item) => generateIssueTemplateItem(item))
    };

    issueTemplateSchema
      .validate(formData, {
        abortEarly: false
      })
      .then((valid) => {
        addIssueTemplate({
          variables: {
            input: postBody
          },
          ...impersonateHeader(useAdminToken)
        }).then((res) => setShowDoneModal(true));
      })
      .catch((err) => setValidationErrors(err.errors));
  };

  const onCloseModal = () => {
    setShowDoneModal(false);
    resetForm();
  };

  return (
    <OverviewWrapper>
      <IssueTemplateActionbar onAddTemplateItem={addNewItem} />
      <BinderContent>
        {showDoneModal && (
          <AdminModal
            ref={showDoneModalNode}
            onClose={onCloseModal}
            label={'Create Issue template template'}
            name={'Issue Template template'}
            // @ts-ignore
            Component={({ onClose }) => (
              <ModalWrapper>
                <h2>Issue Template created</h2>
                <Button
                  type="button"
                  btnType="regular"
                  btnStyle={'primary'}
                  label="OK"
                  onClick={onClose}
                />
              </ModalWrapper>
            )}
          />
        )}
        <FormContainer onSubmit={handleSubmit}>
          {validationErrors.map((validationError, i) => (
            <ErrorField key={i}>{validationError}</ErrorField>
          ))}
          <Container>
            <SearchableDropDown
              {...{
                selectedType: selectedOrg,
                setSelectedType: setSelectedOrg
              }}
              label="Organization"
              data={orgs.data && orgs.data.get}
              loading={orgs.networkStatus !== NetworkStatus.ready}
              type="organization"
            />
            <TextInput
              changeValue={(e) => setTemplateName(e.target.value)}
              value={templateName}
              type="text"
              label="Template Name"
              required={true}
            />
          </Container>

          {selectedOrg.organization !== null &&
            templateName.length >= 3 &&
            roleTypes !== null &&
            renderForm()}

          <StyledButton
            data-test="submit-btn"
            btnType="regular"
            btnStyle="primary"
            label="Submit"
            type="submit"
          />
        </FormContainer>
      </BinderContent>
    </OverviewWrapper>
  );
};

export default CreateIssueTemplate;
